import { Component, computed, effect, inject, input, LOCALE_ID, OnInit, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CustomerProductFilters } from '../../../../../pages/user-dashboard/user-products/customer-product.model';
import { LookupVTO } from '../../../../../pages/admin-dashboard/admin-companies/admin-companies.model';
import { SubcategoryResultSet } from '../../../../../pages/admin-dashboard/categories/category.model';
import { ProductsLookupService } from '../../../../../services/product/lookup.service';
import { CategoryService } from '../../../../../services/category/category.service';
import { FilterItemSearch } from '../../../../../shared/ui/shared-filters/filter-item-search/filter-item-search';
import { FilterSelectComponent } from '../../../../../shared/ui/shared-filters/filter-select-component/filter-select-component';
import { FilterSortingComponent } from '../../../../../shared/ui/shared-filters/filter-sorting-component/filter-sorting-component';
import { FilterPriceRangeInput } from '../../../../../shared/ui/shared-filters/filter-price-range-input/filter-price-range-input';

@Component({
  selector: 'app-product-filters',
  standalone: true,
  imports: [
    FormsModule,
    FilterItemSearch,
    FilterSelectComponent,
    FilterSortingComponent,
    FilterPriceRangeInput,
  ],
  templateUrl: './product-filters.html',
  styleUrl: './product-filters.scss',
})
export class ProductFilters implements OnInit {
  private locale = inject(LOCALE_ID);
  private lookupService = inject(ProductsLookupService);
  private categoryService = inject(CategoryService);

  presetCategoryId = input<number | undefined>(undefined);
  presetMfgCompanyId = input<number | undefined>(undefined);

  applyFilters = output<CustomerProductFilters>();
  resetFilters = output<void>();
  toggleSortOrder = output<void>();

  categories = signal<LookupVTO[]>([]);
  subCategories = signal<SubcategoryResultSet | undefined>(undefined);
  mfgCompanies = signal<LookupVTO[]>([]);

  quickSearch = signal('');
  selectedCategoryId = signal<number | undefined>(undefined);
  selectedSubCategoryId = signal<number | undefined>(undefined);
  selectedMfgCompanyId = signal<number | undefined>(undefined);
  selectedAvailability = signal<string | undefined>(undefined);
  businessPriceFrom = signal<number | null>(null);
  businessPriceTo = signal<number | null>(null);
  customerPriceFrom = signal<number | null>(null);
  customerPriceTo = signal<number | null>(null);
  orderBy = signal<string>('CREATION_DATE');
  orderDir = signal<'ASC' | 'DESC'>('DESC');

  sortOptions = computed(() => [
    { value: 'CREATION_DATE', labelEn: 'Creation Date', labelAr: 'تاريخ الإنشاء' },
    { value: 'LAST_MODIFICATION_DATE', labelEn: 'Last Modified', labelAr: 'آخر تعديل' },
    { value: 'TITLE', labelEn: 'Product Title', labelAr: 'اسم الصنف' },
    { value: 'CUSTOMER_PRICE', labelEn: 'People Price', labelAr: 'سعر الجمهور' },
    { value: 'BUSINESS_PRICE', labelEn: 'Pharmacy Price', labelAr: 'سعر الصيدلية' },
    { value: 'EXPIRY_DATE', labelEn: 'Expiry Date', labelAr: 'تاريخ الانتهاء' },
    { value: 'MFG_DATE', labelEn: 'Manufacturing Date', labelAr: 'تاريخ الانتاج' },
  ]);

  availabilityOptions = signal([
    { id: 'in_stock', titleEn: 'In Stock', titleAr: 'متوفر' },
    { id: 'out_of_stock', titleEn: 'Out of Stock', titleAr: 'غير متوفر' },
  ]);

  isArabic = computed(() => this.locale.startsWith('ar'));

  constructor() {
    effect(() => {
      const id = this.presetCategoryId();
      if (id !== undefined) {
        this.onCategorySelect(id);
      }
    });

    effect(() => {
      const id = this.presetMfgCompanyId();
      if (id !== undefined) {
        this.selectedMfgCompanyId.set(id);
      }
    });
  }

  ngOnInit() {
    this.lookupService.getLookupCategories().subscribe((res) => this.categories.set(res.list));
    this.lookupService.getLookupMFGCompanies().subscribe((res) => this.mfgCompanies.set(res.list));

    if (this.presetCategoryId() !== undefined) {
      this.selectedCategoryId.set(this.presetCategoryId());
    }

    if (this.presetMfgCompanyId() !== undefined) {
      this.selectedMfgCompanyId.set(this.presetMfgCompanyId());
    }

    this.onApply();
  }

  onCategorySelect(id: number | undefined) {
    this.selectedCategoryId.set(id);
    this.selectedSubCategoryId.set(undefined);
    this.subCategories.set(undefined);

    if (id) {
      this.categoryService.getSubcategories({ categoryId: id }).subscribe({
        next: (res) => this.subCategories.set(res),
        error: () => console.error('Error loading subcategories'),
      });
    }
  }

  onToggleSortOrder() {
    this.orderDir.update((d) => (d === 'ASC' ? 'DESC' : 'ASC'));
    this.onApply();
  }

  onApply() {
    const av = this.selectedAvailability();
    const isOutOfStock = av === 'out_of_stock' ? true : av === 'in_stock' ? false : undefined;

    let bpFromVal = this.businessPriceFrom() ?? 0;
    let bpToVal = this.businessPriceTo() ?? 0;

    if (bpFromVal > bpToVal && bpToVal > 0) {
      const tmp = bpFromVal;
      bpFromVal = bpToVal;
      bpToVal = tmp;
    }

    if (bpFromVal < 0) bpFromVal = 0;
    if (bpToVal < 0) bpToVal = 0;

    let cpFromVal = this.customerPriceFrom() ?? 0;
    let cpToVal = this.customerPriceTo() ?? 0;

    if (cpFromVal > cpToVal && cpToVal > 0) {
      const tmp = cpFromVal;
      cpFromVal = cpToVal;
      cpToVal = tmp;
    }

    if (cpFromVal < 0) cpFromVal = 0;
    if (cpToVal < 0) cpToVal = 0;

    const bpFrom = bpFromVal > 0 ? bpFromVal : undefined;
    const bpTo = bpToVal > 0 ? bpToVal : undefined;
    const cpFrom = cpFromVal > 0 ? cpFromVal : undefined;
    const cpTo = cpToVal > 0 ? cpToVal : undefined;

    this.applyFilters.emit({
      quickSearch: this.quickSearch() || undefined,
      categoryId: this.selectedCategoryId(),
      subCategoryId: this.selectedSubCategoryId(),
      mfgCompanyId: this.selectedMfgCompanyId(),
      businessPriceFrom: bpFrom,
      businessPriceTo: bpTo,
      customerPriceFrom: cpFrom,
      customerPriceTo: cpTo,
      isOutOfStock,
      orderBy: this.orderBy(),
      orderDir: this.orderDir(),
    });
  }

  onReset() {
    this.quickSearch.set('');
    this.selectedCategoryId.set(undefined);
    this.selectedSubCategoryId.set(undefined);
    this.selectedMfgCompanyId.set(undefined);
    this.selectedAvailability.set(undefined);
    this.businessPriceFrom.set(null);
    this.businessPriceTo.set(null);
    this.customerPriceFrom.set(null);
    this.customerPriceTo.set(null);
    this.orderBy.set('CREATION_DATE');
    this.orderDir.set('DESC');
    this.subCategories.set(undefined);
    this.resetFilters.emit();
  }
}
